import { ReactNode, useEffect, useMemo, useRef, useState } from "react";
import { ArrowLeft, RefreshCw, Settings } from "lucide-react";
import {
  CartesianGrid,
  ComposedChart,
  Legend,
  Line,
  ReferenceLine,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";
import { getErrorMessage } from "../../api/client";
import { useAuth } from "../../context/AuthContext";
import { DettaglioCassaforteRitiri, dettaglioCassaforteRitiri, msDataAznext } from "../../api/ritiri";
import { formattaDataAznext } from "../../api/ritiri";
import { CardRitiri, PulsanteRitiri } from "../../components/ritiri/TestataRitiri";
import { AiutoRitiri } from "../../components/ritiri/AiutoRitiri";
import { BadgeEsito, BarraRiempimento, euro, giornoBreve, nomeGiorno, numero, percento } from "./presentazione";
import { BARRE, DATE, SiglaData } from "./testiLegenda";
import { RichiesteCassaforte } from "./RichiesteCassaforte";

/**
 * Dettaglio della cassaforte selezionata (§ 7 punto 3): motivazioni, etichette,
 * richiesta di ritiro con le bozze email, dati e grafico. Tutto viene da
 * GET /api/ritiri/casseforti/:idMacchina; il grafico colloca nel tempo i punti
 * che il backend ha già calcolato.
 */

/** Il titolo del dettaglio: la finestra modale del telefono vi punta con `aria-labelledby`. */
export const ID_TITOLO_DETTAGLIO = "dettaglio-cassaforte-titolo";

interface Props {
  idMacchina: string;
  /** Cambia quando la situazione viene riletta: il dettaglio si rilegge con lei. */
  versione: number;
  onChiudi: () => void;
  /** Apre la finestra delle impostazioni (la gestisce la pagina: mai due finestre insieme). */
  onImpostazioni: () => void;
  /** Dopo una scrittura sulle richieste: la pagina rilegge la situazione, e con lei questo dettaglio. */
  onCambiata: () => void;
}

export function DettaglioCassaforte({ idMacchina, versione, onChiudi, onImpostazioni, onCambiata }: Props) {
  const { hasPermission } = useAuth();
  const [dettaglio, setDettaglio] = useState<DettaglioCassaforteRitiri | null>(null);
  const [errore, setErrore] = useState<string | null>(null);
  const [tentativo, setTentativo] = useState(0);
  const titolo = useRef<HTMLHeadingElement>(null);

  useEffect(() => {
    let valida = true;
    setDettaglio(null);
    setErrore(null);
    dettaglioCassaforteRitiri(idMacchina)
      .then((d) => valida && setDettaglio(d))
      .catch((err) => valida && setErrore(getErrorMessage(err, "Impossibile caricare il dettaglio della cassaforte.")));
    return () => {
      valida = false;
    };
  }, [idMacchina, versione, tentativo]);

  // Sul telefono il dettaglio copre la pagina: il fuoco va al suo titolo.
  useEffect(() => {
    titolo.current?.focus();
  }, [idMacchina]);

  return (
    <div className="space-y-4" aria-live="polite">
      <div className="flex items-start gap-3">
        <button
          type="button"
          onClick={onChiudi}
          className="mt-0.5 inline-flex h-9 shrink-0 items-center gap-1.5 rounded-[11px] border border-ritiri-bordo-forte bg-white px-3 text-sm font-semibold text-ink hover:bg-ritiri-hover lg:hidden"
        >
          <ArrowLeft className="h-4 w-4" aria-hidden="true" />
          Elenco
        </button>
        <div className="min-w-0 flex-1">
          <h2 ref={titolo} id={ID_TITOLO_DETTAGLIO} tabIndex={-1} className="text-xl font-extrabold leading-tight text-ink outline-none">
            {dettaglio?.nome_punto ?? idMacchina}
          </h2>
          <p className="mt-0.5 font-plex text-xs text-ritiri-th-testo">{idMacchina}</p>
        </div>
        {hasPermission("ritiri:update") && (
          <button
            type="button"
            onClick={onImpostazioni}
            aria-label="Impostazioni"
            className="mt-0.5 inline-flex h-9 shrink-0 items-center gap-1.5 rounded-[11px] border border-ritiri-bordo-forte bg-white px-3 text-sm font-semibold text-ink hover:bg-ritiri-hover"
          >
            <Settings className="h-4 w-4" aria-hidden="true" />
            {/* Sul telefono solo l'icona: il titolo ha bisogno dello spazio. */}
            <span className="hidden sm:inline">Impostazioni</span>
          </button>
        )}
        <button
          type="button"
          onClick={onChiudi}
          className="hidden text-sm font-semibold text-ritiri-th-testo hover:text-ink lg:inline"
        >
          Chiudi
        </button>
      </div>

      {errore && (
        <CardRitiri>
          <p className="text-sm text-ink">{errore}</p>
          <PulsanteRitiri type="button" variante="secondario" className="mt-3 h-9 px-3.5" onClick={() => setTentativo((t) => t + 1)}>
            <RefreshCw className="h-4 w-4" aria-hidden="true" />
            Riprova
          </PulsanteRitiri>
        </CardRitiri>
      )}
      {!errore && !dettaglio && <p className="text-sm text-ritiri-th-testo">Caricamento del dettaglio…</p>}
      {dettaglio && <Contenuto d={dettaglio} onCambiata={onCambiata} onImpostazioni={onImpostazioni} />}
    </div>
  );
}

function Contenuto({ d, onCambiata, onImpostazioni }: { d: DettaglioCassaforteRitiri; onCambiata: () => void; onImpostazioni: () => void }) {
  const localita = [d.localita, d.provincia ? `(${d.provincia})` : null].filter(Boolean).join(" ");
  return (
    <div className="ritiri-rise space-y-4">
      <CardRitiri>
        <div className="flex flex-wrap items-center gap-2">
          <BadgeEsito esito={d.esito} testo={d.esito_testo} conAiuto />
          <span className="text-sm text-ritiri-th-testo">
            {[d.codice_gdo ? `GDO ${d.codice_gdo}` : null, localita || null].filter(Boolean).join(" · ")}
          </span>
        </div>
        {d.motivazioni.length > 0 ? (
          <ul className="mt-4 space-y-2 text-sm leading-relaxed text-ink">
            {d.motivazioni.map((m) => (
              <li key={m} className="rounded-xl bg-ritiri-th-fondo px-3.5 py-2.5">
                {m}
              </li>
            ))}
          </ul>
        ) : (
          <p className="mt-3 text-sm text-ritiri-th-testo">Nessuna motivazione: la cassaforte non richiede azioni.</p>
        )}
        {d.etichette.length > 0 && (
          <div className="mt-4">
            <h3 className="mb-2 text-[11px] font-bold uppercase tracking-wide text-ritiri-th-testo">Etichette</h3>
            <ul className="space-y-1.5 text-sm text-ink">
              {d.etichette.map((e) => (
                <li key={e.codice} className="flex gap-2">
                  <span className="mt-2 h-1.5 w-1.5 shrink-0 rounded-full bg-accent" aria-hidden="true" />
                  <span>{e.testo}</span>
                </li>
              ))}
            </ul>
          </div>
        )}
        <div className="mt-4 space-y-3">
          <BarraRiempimento etichetta="Adesso" punto={d.giacenza} aiuto={{ testo: BARRE.adesso, nome: "Adesso" }} limiti={d.dati.limiti} />
          <BarraRiempimento
            etichetta={`A P2 ${giornoBreve(d.P2)}`}
            punto={d.previsto_p2}
            aiuto={{ testo: BARRE.aP2, nome: "A P2" }}
            limiti={d.dati.limiti}
            stima
          />
        </div>
      </CardRitiri>

      <RichiesteCassaforte d={d} onCambiata={onCambiata} onImpostazioni={onImpostazioni} />

      <CardRitiri titolo="Riempimento e previsione">
        <GraficoRiempimento d={d} />
      </CardRitiri>

      <CardRitiri titolo="Dati">
        <Dati d={d} />
      </CardRitiri>
    </div>
  );
}

// ----------------------------------------------------------------------------
// Dati
// ----------------------------------------------------------------------------

/** Il fattore, e in chiaro quando è neutro perché alla categoria mancano i dati. */
function FattoreStagionale({ f }: { f: DettaglioCassaforteRitiri["dati"]["fattore_stagionale"] }) {
  if (!f) return <>—</>;
  const neutri = (["e1", "e2"] as const).filter((e) => f.senza_dati[e]).map((e) => e.toUpperCase());
  const nota = f.senza_dati.ultime_12_settimane
    ? "Neutri: la categoria non ha versamenti nelle ultime 12 settimane."
    : neutri.length === 2
      ? "Neutri: la categoria non ha versamenti nei mesi di E1 ed E2."
      : neutri.length === 1
        ? `${neutri[0]} neutro: la categoria non ha versamenti nel mese di ${neutri[0]}.`
        : null;
  return (
    <>
      {numero(f.e1, 2)} / {numero(f.e2, 2)}
      {nota && <span className="block font-sans text-xs text-ritiri-th-testo">{nota}</span>}
    </>
  );
}

function Dati({ d }: { d: DettaglioCassaforteRitiri }) {
  const x = d.dati;
  // Terzo elemento: la sigla della data, per il "?" con la sua definizione.
  const voci: [string, ReactNode, SiglaData?][] = [
    ["Scadenza richiesta (D)", `${giornoBreve(d.D)} ore 16:00`, "D"],
    ["Primo ritiro utile (P1)", giornoBreve(d.P1), "P1"],
    ["Ritiro successivo (P2)", giornoBreve(d.P2), "P2"],
    ["Giacenza", `${numero(d.giacenza.pezzi)} banconote · ${euro(d.giacenza.euro)}`],
    ["Capienza (pezzi)", numero(x.limiti.pezzi)],
    ["Limite in euro (90% del massimale)", euro(x.limiti.euro)],
    ["Previsione a P1 (prudente)", percento(d.p1)],
    ["Previsione a P2 (prudente / media)", `${percento(d.p2)} / ${percento(d.c2)}`],
    ["Categoria di attività", x.categoria_attivita],
    ["Fattore stagionale (E1 / E2)", <FattoreStagionale f={x.fattore_stagionale} />],
    ["Settimane di storico usate", numero(x.finestre)],
    ["Versato medio giornaliero", euro(x.versato_medio_giornaliero)],
    ["Intervallo economico", x.intervallo_economico === null ? "—" : `${x.intervallo_economico} giorni`],
    ["Giorni dall'ultimo ritiro al P1", numero(x.giorni_al_p1)],
    ["Trasportatore", x.vettore ?? (d.trasportatori.map((t) => t.nome).join(", ") || "—")],
    ["Clienti", d.clienti.join(", ") || "—"],
    ["Ultimo ritiro", formattaDataAznext(x.ultimo_prelievo)],
    ["Ultimo versamento", formattaDataAznext(x.ultimo_versamento)],
    ["Ultimo versamento ricevuto", formattaDataAznext(x.ultimo_versamento_ricevuto)],
    ["Ritiro fisso proposto", x.ritiro_fisso_proposto ? `ogni ${nomeGiorno(x.ritiro_fisso_proposto)}` : "—"],
  ];
  return (
    <dl className="grid gap-x-6 gap-y-2.5 text-sm sm:grid-cols-2">
      {voci.map(([dt, dd, sigla]) => (
        <div key={dt} className="min-w-0">
          <dt className="flex items-center gap-1 text-[11px] font-bold uppercase tracking-wide text-ritiri-th-testo">
            {dt}
            {sigla && <AiutoRitiri testo={DATE[sigla].testo} etichetta={`Aiuto: ${sigla}`} />}
          </dt>
          <dd className="break-words font-plex text-ink">{dd}</dd>
        </div>
      ))}
    </dl>
  );
}

// ----------------------------------------------------------------------------
// Grafico (§ 7 punto 3): curva di 21 giorni, proiezioni a E1 ed E2, linee 100% e 85%
// ----------------------------------------------------------------------------

interface PuntoGrafico {
  x: number;
  riempimento?: number | null;
  medio?: number | null;
  prudente?: number | null;
}

const cento = (q: number | null | undefined) => (q === null || q === undefined ? null : Math.round(q * 1000) / 10);

/** Asse dei tempi: millisecondi con i componenti UTC = ora italiana (come `msDataAznext`). */
function etichettaGiorno(ms: number): string {
  const d = new Date(ms);
  return `${String(d.getUTCDate()).padStart(2, "0")}/${String(d.getUTCMonth() + 1).padStart(2, "0")}`;
}

function etichettaOra(ms: number): string {
  const d = new Date(ms);
  return `${etichettaGiorno(ms)} ${String(d.getUTCHours()).padStart(2, "0")}:${String(d.getUTCMinutes()).padStart(2, "0")}`;
}

function movimentoRidotto(): boolean {
  return typeof window !== "undefined" && window.matchMedia?.("(prefers-reduced-motion: reduce)").matches;
}

function GraficoRiempimento({ d }: { d: DettaglioCassaforteRitiri }) {
  const dati = useMemo(() => {
    const adesso = msDataAznext(d.adesso);
    const punti: PuntoGrafico[] = [];
    d.curva.forEach((c, i) => {
      // Ogni giorno vale a fine giornata; l'ultimo è oggi, fino ad adesso.
      const x = i === d.curva.length - 1 && adesso !== null ? adesso : msDataAznext(`${c.giorno} 23:59`);
      if (x !== null) punti.push({ x, riempimento: cento(c.percentuale) });
    });
    if (d.previsioni && adesso !== null) {
      const ultimo = punti[punti.length - 1];
      const partenza = cento(d.giacenza.percentuale);
      if (ultimo && ultimo.x === adesso) Object.assign(ultimo, { medio: partenza, prudente: partenza });
      else punti.push({ x: adesso, medio: partenza, prudente: partenza });
      for (const [quando, p] of [
        [d.E1, d.previsioni.E1],
        [d.E2, d.previsioni.E2],
      ] as const) {
        const x = msDataAznext(quando);
        if (x !== null) punti.push({ x, medio: cento(p.medio.percentuale), prudente: cento(p.prudente.percentuale) });
      }
    }
    return punti;
  }, [d]);

  const senzaLimite = d.curva.every((c) => c.percentuale === null);
  if (senzaLimite) {
    return (
      <p className="text-sm text-ritiri-th-testo">
        Grafico non disponibile: senza capienza né valore assicurato non c'è un limite su cui misurare il riempimento.
      </p>
    );
  }

  const massimo = Math.max(
    110,
    ...dati.flatMap((p) => [p.riempimento, p.medio, p.prudente].filter((v): v is number => typeof v === "number"))
  );
  const cima = Math.ceil(massimo / 25) * 25;
  const animato = !movimentoRidotto();
  const e1 = msDataAznext(d.E1);
  const e2 = msDataAznext(d.E2);

  return (
    <figure>
      <div className="h-64 w-full sm:h-72" role="img" aria-label={`Riempimento degli ultimi 21 giorni e previsione a E1 ed E2 per ${d.nome_punto}`}>
        <ResponsiveContainer width="100%" height="100%">
          <ComposedChart data={dati} margin={{ top: 8, right: 12, bottom: 0, left: -12 }}>
            <CartesianGrid stroke="#EDEFEB" vertical={false} />
            <XAxis
              dataKey="x"
              type="number"
              scale="time"
              domain={["dataMin", "dataMax"]}
              tickFormatter={etichettaGiorno}
              tick={{ fontSize: 11, fill: "#5D6670" }}
              minTickGap={24}
            />
            <YAxis
              domain={[0, cima]}
              ticks={Array.from({ length: cima / 25 + 1 }, (_, i) => i * 25)}
              tickFormatter={(v: number) => `${v}%`}
              tick={{ fontSize: 11, fill: "#5D6670" }}
              width={48}
            />
            <Tooltip
              labelFormatter={(v) => etichettaOra(Number(v))}
              formatter={(v: number) => `${numero(v, 1)}%`}
              contentStyle={{ borderRadius: 12, borderColor: "#E3E6E1", fontSize: 12 }}
            />
            <Legend wrapperStyle={{ fontSize: 12 }} />
            <ReferenceLine y={100} stroke="#dc2626" strokeDasharray="4 3" label={{ value: "100%", position: "insideTopLeft", fontSize: 11, fill: "#B3261E" }} />
            <ReferenceLine y={85} stroke="#d99730" strokeDasharray="4 3" label={{ value: "85%", position: "insideTopLeft", fontSize: 11, fill: "#6B4700" }} />
            {/* E1 ed E2 distano spesso un giorno: le etichette si sovrapporrebbero, le spiega la didascalia. */}
            {e1 !== null && <ReferenceLine x={e1} stroke="#C9D1DB" />}
            {e2 !== null && <ReferenceLine x={e2} stroke="#C9D1DB" />}
            <Line
              name="Riempimento"
              dataKey="riempimento"
              stroke="#18273d"
              strokeWidth={2}
              dot={false}
              connectNulls
              isAnimationActive={animato}
            />
            <Line
              name="Previsione media"
              dataKey="medio"
              stroke="#2c4363"
              strokeDasharray="5 4"
              strokeWidth={2}
              dot={{ r: 3 }}
              connectNulls
              isAnimationActive={animato}
            />
            <Line
              name="Previsione prudente"
              dataKey="prudente"
              stroke="#B45309"
              strokeDasharray="5 4"
              strokeWidth={2}
              dot={{ r: 3 }}
              connectNulls
              isAnimationActive={animato}
            />
          </ComposedChart>
        </ResponsiveContainer>
      </div>
      <figcaption className="mt-2 text-xs text-ritiri-th-testo">
        {d.previsioni ? (
          <>
            E1 = {giornoBreve(d.P1)} ore 14:00, E2 = {giornoBreve(d.P2)} ore 14:00. A E1: media {percento(d.previsioni.E1.medio.percentuale)},
            prudente {percento(d.previsioni.E1.prudente.percentuale)}. A E2: media {percento(d.previsioni.E2.medio.percentuale)}, prudente{" "}
            {percento(d.previsioni.E2.prudente.percentuale)}. Percentuali sul limite che scatta per primo.
          </>
        ) : (
          "Nessuna previsione per questa cassaforte: si mostra solo la curva degli ultimi 21 giorni."
        )}
      </figcaption>
    </figure>
  );
}
